/**
 * CENTRAL threshold config — the single place that decides green / amber / red for every health
 * card, so the active and finished pages stay consistent and "what earns red" is tunable in one spot.
 *
 * Reputation rule (deliberate): routine aging tops out at AMBER, no matter how far a number slips.
 * RED is reserved for genuine urgency and is earned ONLY by the PRESENCE of a Critical-severity
 * security item — never by a count, percentage, or accumulation crossing a threshold. That way a
 * high VOLUME of issues (which could look like we shipped poor quality) stays amber, while a single
 * Critical item (an urgent external change to patch) escalates to red — the honest maintenance signal.
 * The "getting worse" story is carried by the TREND numbers, not by everything turning red.
 */
import { FreshnessBuckets, HealthStatus, SeveritySplit } from './types';
import { compareVersions, isPrerelease, isSameVersion, versionCore, versionLineLabel, wholeMonthsSince } from './util';

export const THRESHOLDS = {
  // Green below the cutoff, amber at/above it. None of these ever escalate to red on their own.
  dependenciesOutdatedPctAmberAtOrAbove: 10,
  performanceScoreGreenAtOrAbove: 90,
  errorRatePctAmberAtOrAbove: 0.5,
  crashFreeSessionsPctGreenAtOrAbove: 99.5,
};

// Security is the ONLY card that can go red — and only when a Critical-severity item is present.
export const securityStatus = (s: SeveritySplit): HealthStatus => {
  if (s.critical > 0) return 'risk';
  const open = s.critical + s.high + s.medium + s.low;
  return open > 0 ? 'watch' : 'good';
};

export const dependencyStatus = (outdatedPct: number): HealthStatus =>
  outdatedPct >= THRESHOLDS.dependenciesOutdatedPctAmberAtOrAbove ? 'watch' : 'good';

// Named because the card states these to the client, so the sentence and the colour share one source.
export const DEPENDENCIES_UNSUPPORTED_SHARE_RED = 0.05;
export const DEPENDENCIES_UNSUPPORTED_COUNT_AMBER = 1;
export const DEPENDENCIES_BEHIND_SHARE_AMBER = 0.2;

/** The shares the dependencies card states, as whole percentages, so its sentence cannot drift from the rule. */
export const dependenciesThresholdShares = (): { behind: number; unsupportedRed: number } => ({
  behind: Math.round(DEPENDENCIES_BEHIND_SHARE_AMBER * 100),
  unsupportedRed: Math.round(DEPENDENCIES_UNSUPPORTED_SHARE_RED * 100),
});

export const dependenciesCountStatus = (c: {
  packagesUsed: number;
  behind: number;
  unsupported: number;
}): HealthStatus => {
  const used = c.packagesUsed || 1;
  if (c.unsupported / used >= DEPENDENCIES_UNSUPPORTED_SHARE_RED) return 'risk';
  if (c.unsupported >= DEPENDENCIES_UNSUPPORTED_COUNT_AMBER || c.behind / used >= DEPENDENCIES_BEHIND_SHARE_AMBER)
    return 'watch';
  return 'good';
};

export const performanceStatus = (score: number): HealthStatus =>
  score >= THRESHOLDS.performanceScoreGreenAtOrAbove ? 'good' : 'watch';

export const errorRateStatus = (pct: number): HealthStatus =>
  pct >= THRESHOLDS.errorRatePctAmberAtOrAbove ? 'watch' : 'good';

// Routine reliability metric — tops out at amber like the other "quality" numbers, never red.
export const crashFreeStatus = (pct: number): HealthStatus =>
  pct >= THRESHOLDS.crashFreeSessionsPctGreenAtOrAbove ? 'good' : 'watch';

// Response time drives a Fast / Moderate / Slow verdict (and the scale zones on the card). Under
// ~0.5s feels instant; a genuinely slow app (≥1.5s) is a real UX problem, so it can reach red here.
export const RESPONSE_FAST_BELOW_MS = 500;
export const RESPONSE_SLOW_AT_OR_ABOVE_MS = 1500;
export const responseTimeStatus = (ms: number): HealthStatus => {
  if (ms < RESPONSE_FAST_BELOW_MS) return 'good';
  if (ms < RESPONSE_SLOW_AT_OR_ABOVE_MS) return 'watch';
  return 'risk';
};

// Platform support is the one NON-security card that can go red — but only when a core building
// block (runtime, framework, database) is out of support or about to be. That's genuine urgency (no
// more security patches, and a real upgrade to do), in the same spirit as a Critical CVE. A
// comfortable runway is green, a heads-up window is amber; red is reserved for imminent/expired
// support so it stays rare. Negative days = already past end-of-life → red.
export const SUPPORT_RED_WITHIN_DAYS = 30;
export const SUPPORT_AMBER_WITHIN_DAYS = 180;
export const supportStatus = (daysUntilEol: number): HealthStatus => {
  if (daysUntilEol <= SUPPORT_RED_WITHIN_DAYS) return 'risk';
  if (daysUntilEol <= SUPPORT_AMBER_WITHIN_DAYS) return 'watch';
  return 'good';
};

// Green while the majority of repos are still actively worked on; amber as that share declines.
export const freshnessStatus = (b: FreshnessBuckets): HealthStatus => {
  const total = b.active + b.inactive + b.dormant || 1;
  return b.active / total > 0.5 ? 'good' : 'watch';
};

// A bare count (e.g. one Aikido check's issues) — never red, since volume alone can't earn it.
export const countStatus = (n: number): HealthStatus => (n > 0 ? 'watch' : 'good');

// ---- Dependency currency ----
// Calendar months, so the grading agrees with the "for N" text whatever the leap years; the card says "a year".
export const DEPENDENCY_UNSUPPORTED_AFTER_MONTHS = 12;

export type DependencyCurrency = 'current' | 'behind' | 'unsupported' | 'unknown';

interface GradedFacts {
  versionInUse: string;
  latestVersion: string | null;
  isDeprecated: boolean;
  isLatestDeprecated: boolean;
  behindSince: string | null;
  nextMajorSince: string | null;
  lineLastReleasedAt: string | null;
}

/** A prerelease or `next` line in use can be newer than the registry's default release. */
export const isAheadOfLatest = (dependency: Pick<GradedFacts, 'versionInUse' | 'latestVersion'>): boolean =>
  !!dependency.latestVersion && compareVersions(dependency.versionInUse, dependency.latestVersion) > 0;

/** A newer major out a year, and no release on the line in use for a year: both, as yargs 17.x is still maintained. */
export const isLineUnsupported = (dependency: GradedFacts, now: Date): boolean =>
  !!dependency.nextMajorSince &&
  !!dependency.lineLastReleasedAt &&
  wholeMonthsSince(dependency.nextMajorSince, now) >= DEPENDENCY_UNSUPPORTED_AFTER_MONTHS &&
  wholeMonthsSince(dependency.lineLastReleasedAt, now) >= DEPENDENCY_UNSUPPORTED_AFTER_MONTHS;

/** Behind needs a newer stable release (`behindSince`); a prerelease default alone is not one. */
export const hasNoNewerStable = (dependency: Pick<GradedFacts, 'latestVersion' | 'behindSince'>): boolean =>
  !!dependency.latestVersion && isPrerelease(dependency.latestVersion) && !dependency.behindSince;

/** Only a newer patch on the same line: routine upkeep, so it grades current while the row still says so. */
export const isPatchBehindOnly = (dependency: Pick<GradedFacts, 'versionInUse' | 'latestVersion'>): boolean => {
  const latest = dependency.latestVersion ?? '';
  const [inUseCore, latestCore] = [versionCore(dependency.versionInUse), versionCore(latest)];
  return (
    !!inUseCore &&
    !!latestCore &&
    !isPrerelease(dependency.versionInUse) &&
    !isPrerelease(latest) &&
    versionLineLabel(dependency.versionInUse) === versionLineLabel(latest) &&
    inUseCore[1] === latestCore[1] &&
    inUseCore[2] < latestCore[2]
  );
};

/** Deprecation is checked before versions, or a dead package on its own last release would score current. */
export const dependencyCurrency = (dependency: GradedFacts, now: Date = new Date()): DependencyCurrency => {
  if (dependency.isDeprecated || dependency.isLatestDeprecated) return 'unsupported';
  if (!dependency.latestVersion) return 'unknown';
  if (
    isSameVersion(dependency.versionInUse, dependency.latestVersion) ||
    isAheadOfLatest(dependency) ||
    hasNoNewerStable(dependency) ||
    isPatchBehindOnly(dependency)
  )
    return 'current';
  return isLineUnsupported(dependency, now) ? 'unsupported' : 'behind';
};
